import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reauthenticateWithCredential, EmailAuthProvider, updateEmail, updatePassword } from 'firebase/auth';
import { AnimatePresence, motion } from 'motion/react';
import { useAuth } from '../../../utils/Auth';
import { useGetStripeName } from '../../../utils/hooks/useGetStripeName';
import API from '../../../API';
import { collapseVariants, variants, transition } from '../variants';
import styles from '../Settings.module.css';

export default function Account() {
    const navigate = useNavigate();
    const { orgObj, userObj, currentUser } = useAuth();
    const { updateUserObject, createStripeCustomerSession } = API;

    const [newEmail, setNewEmail] = useState(currentUser?.email || '');
    const [emailCurrentPassword, setEmailCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [passwordCurrentPassword, setPasswordCurrentPassword] = useState('');
    const [showEmailConfirmPassword, setShowEmailConfirmPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showPasswordCurrent, setShowPasswordCurrent] = useState(false);
    const [message, setMessage] = useState(null);
    const [busy, setBusy] = useState(false);
    const [openSection, setOpenSection] = useState(null);
    const emailSectionRef = useRef(null);

    const planName = useGetStripeName(orgObj?.plan?.stripe_plan);
    const isPrimary = !!(userObj && currentUser && (userObj.role === 'primary' || userObj.org_id === currentUser.uid));

    useEffect(() => {
        if (currentUser?.email) {
            setNewEmail(currentUser.email);
        }
    }, [currentUser?.email]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openSection === 'email' && emailSectionRef.current && !emailSectionRef.current.contains(event.target)) {
                closeEmailEdit();
            }
        };

        if (openSection === 'email') {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openSection]);

    const setTempMessage = (text, type = 'info') => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 3000);
    };

    const reauthWithPassword = async (password) => {
        if (!currentUser?.email) throw new Error('Missing current user email');
        const credential = EmailAuthProvider.credential(currentUser.email, password);
        await reauthenticateWithCredential(currentUser, credential);
    };

    const handleUpdateEmail = async () => {
        if (!newEmail) {
            setTempMessage('Please enter a new email.', 'error');
            return;
        }
        if (!emailCurrentPassword) {
            setTempMessage('Please enter your current password to confirm.', 'error');
            return;
        }
        try {
            setBusy(true);
            await reauthWithPassword(emailCurrentPassword);
            await updateEmail(currentUser, newEmail);
            setTempMessage('Email successfully updated!', 'success');
            setEmailCurrentPassword('');
        } catch (err) {
            console.error(err);
            setTempMessage('Email could not be updated. Please try again.', 'error');
        } finally {
            setBusy(false);
        }
    };

    const handleUpdatePassword = async () => {
        if (!newPassword) {
            setTempMessage('Please enter a new password.', 'error');
            return;
        }
        if (!passwordCurrentPassword) {
            setTempMessage('Please enter your current password to confirm.', 'error');
            return;
        }
        try {
            setBusy(true);
            await reauthWithPassword(passwordCurrentPassword);
            await updatePassword(currentUser, newPassword);
            setTempMessage('Password successfully updated!', 'success');
            setNewPassword('');
            setPasswordCurrentPassword('');
        } catch (err) {
            console.error(err);
            setTempMessage('Password could not be updated. Please try again.', 'error');
        } finally {
            setBusy(false);
        }
    };

    const handleCustomerSession = async () => {
        try {
            if (!userObj?.stripe_id) throw new Error('No Stripe customer');
            const { data: session } = await createStripeCustomerSession({ cust_id: userObj.stripe_id });
            if (session?.url) {
                window.location.assign(session.url);
            } else {
                throw new Error('No session URL');
            }
        } catch (err) {
            console.error(err);
            setTempMessage('Unable to open subscription management.', 'error');
        }
    };

    const handleUpgrade = () => {
        navigate('/organizations/settings');
    };

    const toggleSection = (sectionKey) => {
        setOpenSection((prev) => (prev === sectionKey ? null : sectionKey));
    };

    const closeEmailEdit = () => {
        setOpenSection(null);
        setEmailCurrentPassword('');
        setShowEmailConfirmPassword(false);
        if (currentUser?.email) {
            setNewEmail(currentUser.email);
        }
    };

    if (!userObj || !currentUser) {
        return null;
    }

    return (
        <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Account Details</h3>
            <div className={styles.sectionContent}>
                {/* Update Email */}
                <div className={styles.fieldGroup} ref={emailSectionRef}>
                    <label className={styles.fieldLabel}>Update Email</label>
                    <div className={styles.inputWithIcon}>
                        <input
                            type='email'
                            className={styles.fieldInput}
                            placeholder='Enter new email'
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            readOnly={openSection !== 'email'}
                            onFocus={() => setOpenSection('email')}
                        />
                        <button
                            type='button'
                            className={styles.toggleIconBtn}
                            onClick={() => setOpenSection('email')}
                            aria-label='Edit email'
                        >
                            <i className='fas fa-edit' />
                        </button>
                    </div>
                    <AnimatePresence initial={false}>
                        {openSection === 'email' && (
                            <motion.div
                                key='email-body'
                                className={styles.fieldBody}
                                style={{ overflow: 'hidden' }}
                                initial='collapsed'
                                animate='open'
                                exit='collapsed'
                                variants={collapseVariants}
                            >
                                <div className={styles.inputWithIcon}>
                                    <input
                                        type={showEmailConfirmPassword ? 'text' : 'password'}
                                        className={styles.fieldInput}
                                        placeholder='Current password to confirm'
                                        value={emailCurrentPassword}
                                        onChange={(e) => setEmailCurrentPassword(e.target.value)}
                                    />
                                    <button
                                        type='button'
                                        className={styles.toggleIconBtn}
                                        onClick={() => setShowEmailConfirmPassword((prev) => !prev)}
                                        aria-label={showEmailConfirmPassword ? 'Hide password' : 'Show password'}
                                    >
                                        <i className={showEmailConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'} />
                                    </button>
                                </div>
                                <div className={styles.actionRow}>
                                    <button className={styles.primaryButton} onClick={handleUpdateEmail} disabled={busy}>Update Email</button>
                                    <button className={styles.secondaryButton} type='button' onClick={closeEmailEdit}>Cancel</button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Update Password */}
                <div className={styles.fieldGroup}>
                    <div className={styles.fieldHeaderRow}>
                        <div>
                            <label className={styles.fieldLabel}>Update Password</label>
                            {openSection !== 'password' && (
                                <p className={styles.fieldDescription}>Set a new password for your account.</p>
                            )}
                        </div>
                        <button
                            className={styles.secondaryButton}
                            onClick={() => toggleSection('password')}
                        >
                            {openSection === 'password' ? 'Close' : 'Edit'}
                        </button>
                    </div>
                    <AnimatePresence initial={false}>
                        {openSection === 'password' && (
                            <motion.div
                                key='password-body'
                                className={styles.fieldBody}
                                style={{ overflow: 'hidden' }}
                                initial='collapsed'
                                animate='open'
                                exit='collapsed'
                                variants={collapseVariants}
                            >
                                <div className={styles.inputWithIcon}>
                                    <input
                                        type={showNewPassword ? 'text' : 'password'}
                                        className={styles.fieldInput}
                                        placeholder='Enter new password'
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value.replace(' ', ''))}
                                    />
                                    <button
                                        type='button'
                                        className={styles.toggleIconBtn}
                                        onClick={() => setShowNewPassword((prev) => !prev)}
                                        aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                                    >
                                        <i className={showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'} />
                                    </button>
                                </div>
                                <div className={styles.inputWithIcon} style={{ marginTop: '.5rem' }}>
                                    <input
                                        type={showPasswordCurrent ? 'text' : 'password'}
                                        className={styles.fieldInput}
                                        placeholder='Current password to confirm'
                                        value={passwordCurrentPassword}
                                        onChange={(e) => setPasswordCurrentPassword(e.target.value)}
                                    />
                                    <button
                                        type='button'
                                        className={styles.toggleIconBtn}
                                        onClick={() => setShowPasswordCurrent((prev) => !prev)}
                                        aria-label={showPasswordCurrent ? 'Hide password' : 'Show password'}
                                    >
                                        <i className={showPasswordCurrent ? 'fas fa-eye-slash' : 'fas fa-eye'} />
                                    </button>
                                </div>
                                <div className={styles.actionRow}>
                                    <button className={styles.primaryButton} onClick={handleUpdatePassword} disabled={busy}>Update Password</button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Subscription (Primary users only) */}
                {isPrimary && (
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Subscription</label>
                        <div className={styles.fieldDisplay}>
                            <span>Plan: {planName || 'Unknown'}</span>
                        </div>
                        <div className={styles.actionRow}>
                            <button className={styles.primaryButton} onClick={handleUpgrade}>Upgrade</button>
                            {userObj?.stripe_id && (
                                <button className={styles.secondaryButton} onClick={handleCustomerSession}>
                                    Manage Subscription
                                </button>
                            )}
                        </div>
                    </div>
                )}

                <AnimatePresence>
                    {message && (
                        <motion.div
                            className={`${styles.message} ${message.type === 'success' ? styles.messageSuccess : ''} ${message.type === 'error' ? styles.messageError : ''}`}
                            variants={variants}
                            initial="initial"
                            animate="animate"
                            exit="initial"
                            transition={transition}
                        >
                            {message.text}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}
